/**
 * Live unread counts for the place rail and the desktop dock: one number
 * per section, and their sum for `window.wtfalchDesktop.setBadgeCount`
 * (agora#219).
 *
 * Each section's count is arithmetic over what its own SDK already
 * reports -- no new server concept, no count of Agora's own. Mail is
 * JMAP's own unread-thread count on the Inbox, kept live through
 * `@wtfalch/email`'s own SSE push (`push()`) rather than a poll: a read
 * receipt, a new message or a delete all change the account's `Mailbox`
 * state, and `push()` says so. Chat is the sum of every *unmuted* room's
 * `unreadCount`. Calendar is upcoming invitations this person has not
 * answered: a participant row of their own with `participationStatus:
 * 'needs-action'`, over the same window `today/rows.ts`'s own "waiting for
 * you" already scans.
 *
 * `App.tsx`'s `AgoraView` owns the mail, chat and calendar clients
 * already; this module only turns what they report into a count and a
 * decision to refetch, so it stays framework-free and unit-testable
 * without rendering anything (`counts.test.ts`).
 */
import type { CalendarClient, CalendarEvent } from '@wtfalch/calendar/calendar';
import type { ChatClient, ChatRoomSummary } from '@wtfalch/chat';
import type { MailboxNode, MailClient } from '@wtfalch/email/mailbox';
import { findRole, mailboxes, push } from '@wtfalch/email/mailbox';

import { type Identity, isUnanswered } from './calendar/participants.ts';
import { todayWindowRange } from './today/rows.ts';

export type Counts = {
  mail: number | null;
  chat: number | null;
  calendar: number | null;
};

/** The company rail's own number (agora#220's rail, once it draws one):
 *  every section's count added up, a section still loading (`null`)
 *  contributing nothing rather than blocking the total. */
export function companyTotal(counts: Counts): number {
  return (counts.mail ?? 0) + (counts.chat ?? 0) + (counts.calendar ?? 0);
}

/** The Inbox's own unread-thread count -- what the Mail tile's badge
 *  shows. `null` when the tree carries no Inbox role at all, the same "the
 *  badge just stays off" the rail already does for a client that has not
 *  loaded. */
export function mailUnreadFromTree(tree: readonly MailboxNode[]): number | null {
  return findRole(tree, 'inbox')?.unreadThreads ?? null;
}

/** Watches the Inbox's unread count live: an immediate read, then a
 *  refetch every time `push()` reports the account's `Mailbox` type has
 *  changed. Returns the function that stops watching. */
export function watchMailUnread(
  client: MailClient,
  onChange: (unread: number | null) => void,
): () => void {
  let live = true;
  const load = () => {
    mailboxes(client)
      .then((tree) => {
        if (live) onChange(mailUnreadFromTree(tree));
      })
      .catch(() => {
        // Mirrors the rail's existing rule: a fetch failure just leaves the
        // badge off, and `Mail`'s own section reports the failure itself.
      });
  };
  load();
  const handle = push(
    client,
    (changed) => {
      if (changed.Mailbox) load();
    },
    { types: ['Mailbox'] },
  );
  return () => {
    live = false;
    handle.close();
  };
}

/** Every unmuted room's `unreadCount`, summed. Deliberately not
 *  `ChatClient.totalUnreadCount()`: that method counts a muted room on
 *  purpose (its own doc comment: a person who mutes a noisy room "still
 *  watches it climb, and still knows to look"), which is right for a
 *  timeline but wrong for a badge whose whole point is "something is
 *  asking to be looked at" -- a room already told not to raise itself is
 *  not that. */
export function chatUnreadTotal(rooms: readonly ChatRoomSummary[]): number {
  return rooms.filter((r) => !r.muted).reduce((sum, r) => sum + r.unreadCount, 0);
}

/** Watches chat's own total live: an immediate read off `rooms()`, then
 *  again on every `onRoomsChanged`. */
export function watchChatUnread(
  client: ChatClient,
  onChange: (unread: number) => void,
): () => void {
  onChange(chatUnreadTotal(client.rooms()));
  return client.onRoomsChanged((rooms) => onChange(chatUnreadTotal(rooms)));
}

/** How many of `events` are invitations `identity` has not answered --
 *  the Calendar tile's own badge. It uses the week grid's own test, so
 *  the badge and the dashed edges always agree. `identity` is `null` before
 *  `CalendarClient.identity()` resolves, which reads as "answered none of
 *  them" rather than "everything", since nothing can be matched yet. */
export function calendarNeedsActionCount(
  events: readonly CalendarEvent[],
  identity: Identity | null,
): number {
  return events.filter((e) => isUnanswered(e, identity)).length;
}

/** Watches Calendar's own count live: fetches who is signed in once, then
 *  an immediate read over the next `daysAhead` days (`todayWindowRange`,
 *  the same window `Today` already scans for "waiting for you"),
 *  refetched whenever `subscribe` reports a `CalendarEvent` change. `now`,
 *  injectable for a test's own clock, defaults to the real one. */
export function watchCalendarNeedsAction(
  client: CalendarClient,
  onChange: (count: number | null) => void,
  options?: { now?: () => Date; daysAhead?: number },
): () => void {
  let live = true;
  let identity: Identity | null = null;
  const now = options?.now ?? (() => new Date());
  const load = () => {
    client
      .listEvents(todayWindowRange(now(), options?.daysAhead))
      .then((events) => {
        if (live) onChange(calendarNeedsActionCount(events, identity));
      })
      .catch(() => {
        // Same rule as Mail and Chat: a fetch failure leaves the badge off.
      });
  };
  client
    .identity()
    .then((id) => {
      identity = id;
      if (live) load();
    })
    .catch(() => {
      // Who is signed in never resolved; the badge just stays off.
    });
  const sub = client.subscribe((changed) => {
    if (changed.CalendarEvent) load();
  });
  return () => {
    live = false;
    sub.close();
  };
}

/** Reports the total to the desktop shell's dock badge, when a desktop
 *  bridge is actually present -- the one host call this module makes
 *  (agora#219; the contract is
 *  `window.wtfalchDesktop.setBadgeCount(count): Promise<void>`, T3's own
 *  side in `apps/desktop`). A rejected promise is swallowed the way every
 *  other best-effort host call in this app already is (`chat/idleness.ts`'s
 *  own `announcePresence`): the dock badge is a courtesy, never something a
 *  person is blocked on. */
export function reportBadgeCount(total: number): void {
  const setBadgeCount = window.wtfalchDesktop?.setBadgeCount;
  if (!setBadgeCount) return;
  void setBadgeCount(total).catch(() => {});
}
